import { useCallback } from "react";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import {
  ScrollArea,
  Space,
  Title,
  Text,
  Anchor,
  Image,
  useMantineTheme,
} from "@mantine/core";
import { useModals } from "@mantine/modals";
import { useTrackStats } from "hooks/useTrackStats";
import { useTranslation } from "react-i18next";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import logo_dark from "../../../assets/main/trackbook_icon_dark.png";
import logo_light from "../../../assets/main/trackbook_icon_light.png";
import { TrackStatsDisplay } from "../../../components/UI/OptionsPanel/TrackStatsDisplay";
import { RoutingPicker } from "./RoutingPicker";
import { TrackpointsList } from "./TrackpointsDisplay";
import "./PlanMenu.css";
import scrollAreaClasses from "components/UI/ScrollArea.module.css";
import classes from "./PlanMenu.module.css";

const styles = {
  div: {
    display: "flex",
    alignItems: "center",
    gap: ".4rem",
    textDecoration: "none",
    marginTop: "auto",
    padding: "0 10px 10px 10px",
    width: "max-content",
  },
  // Explicit height so the layout doesn't shift when the image loads
  image: { width: "1.7rem", height: "1.7rem" },
  title: { fontSize: "1.1rem", position: "relative" as const, top: "-1.5px" },
};

export const PlanMenu = () => {
  const { stats } = useTrackStats();
  const { t } = useTranslation();
  const {
    state: { trackMeta },
  } = useRouting();
  const {
    state: { allowPremiumFeatures, units },
  } = useOptions();
  const modals = useModals();
  const colorScheme = useShownColorScheme();
  const theme = useMantineTheme();
  const isSmallViewport = useIsSmallViewport();

  const handleOnlineServicesClick = useCallback(() => {
    modals.openContextModal("premiumFeatureModal", {
      title: <Text>{`${t("subscription_modal.title")} 🚡`}</Text>,
      innerProps: {},
      padding: 0,
      styles: {
        header: { padding: 20, marginBottom: 0 },
      },
      size: "xl",
    });
  }, [modals, t]);

  const activeColor = colorScheme === "light" ? "#00a800" : "#00d000";
  const inactiveColor = colorScheme === "light" ? "red" : "#fa5252";
  const onlineServicesTextColor = allowPremiumFeatures
    ? activeColor
    : inactiveColor;

  const onlineServicesInfo =
    allowPremiumFeatures !== undefined ? (
      <div
        style={{
          paddingLeft: "10px",
          paddingRight: "10px",
          marginRight: "5px",
          paddingBottom: isSmallViewport ? "5px" : "13px",
          alignContent: "center",
          cursor: !allowPremiumFeatures ? "pointer" : "text",
        }}
        onClick={!allowPremiumFeatures ? handleOnlineServicesClick : undefined}
      >
        {/* "Online services" is the name of the service, shown in English in every language on purpose */}
        <Text size="xs" style={{ display: "inline-block", marginRight: "5px" }}>
          Online services:
        </Text>
        <Text
          size="xs"
          style={{
            display: "inline-block",
            color: onlineServicesTextColor,
          }}
        >
          {allowPremiumFeatures
            ? t("online_services.side_panel_info_text_active_status")
            : t("online_services.side_panel_info_text_inactive_status")}
        </Text>
      </div>
    ) : null;

  return (
    <>
      <ScrollArea
        style={{ flexGrow: 1, padding: "10px 10px 0 10px" }}
        classNames={{
          thumb: scrollAreaClasses.thumb,
          viewport: classes.scrollViewport,
        }}
      >
        <RoutingPicker />
        <Space h="md" />
        <TrackpointsList />
        <Space h="lg" />
        <Text px="xs" py={4} c="dimmed">
          {t("plan_menu.stats_label")}
        </Text>
        <TrackStatsDisplay
          stats={{ ...stats, totalTime: trackMeta.totalTime }}
          unitType={units}
        />
      </ScrollArea>
      <div
        style={{
          paddingTop: "5px",
          display: "flex",
          flexDirection: isSmallViewport ? "column" : "row",
          justifyContent: "space-between",
        }}
      >
        {isSmallViewport ? onlineServicesInfo : null}
        <Anchor href="/" style={styles.div}>
          <Image
            style={styles.image}
            radius="md"
            src={colorScheme === "light" ? logo_light : logo_dark}
            alt="Trackbook logo"
          />
          <Title
            order={1}
            style={{ ...styles.title, fontWeight: 600 }}
            c={colorScheme === "light" ? theme.primaryColor : "#a68cf4"}
          >
            {"Trackbook"}
          </Title>
        </Anchor>
        {!isSmallViewport ? onlineServicesInfo : null}
      </div>
    </>
  );
};
